import { useOne } from '@refinedev/core';
import { Flex, Skeleton, Typography, theme } from 'antd';
import type { PublicationListOut } from '@/api/generated/schemas';
import { ShowLink } from '@/components/common/ShowLink';
import { PublicationCoverThumb } from './PublicationCoverThumb';

const RESOURCE = 'user_catalog.publications';

interface PublicationLazyCellProps {
  publicationId: number;
  /**
   * Title already known by the caller (denormalised on the parent record).
   * When given, no request is made.
   */
  title?: string | null;
  /** Cover already known by the caller; only read when `title` is given. */
  coverUrl?: string | null;
  /** Cover side in px. */
  size?: number;
}

/**
 * Cover + title link for a user-catalog publication referenced by id.
 * Uses the caller's title/cover when known, otherwise fetches them;
 * falls back to the bare id link while loading or if the fetch fails.
 */
export const PublicationLazyCell = ({
  publicationId,
  title,
  coverUrl,
  size = 40,
}: PublicationLazyCellProps) => {
  const { token } = theme.useToken();
  const known = title?.trim() || undefined;
  const { result: fetched, query } = useOne<PublicationListOut>({
    resource: RESOURCE,
    id: publicationId,
    queryOptions: { enabled: !known },
  });

  if (!known && query.isLoading) {
    return (
      <Flex gap={token.marginSM} align="center">
        <Skeleton.Avatar active shape="square" size={size} />
        <Skeleton.Input active size="small" style={{ width: 160 }} />
      </Flex>
    );
  }

  const shownTitle = known ?? fetched?.title ?? `#${publicationId}`;
  const shownCover = known ? coverUrl : fetched?.cover_url;

  return (
    <Flex gap={token.marginSM} align="center" style={{ minWidth: 0 }}>
      <PublicationCoverThumb url={shownCover} size={size} />
      <ShowLink resource={RESOURCE} id={publicationId}>
        <Typography.Text
          style={{ color: 'inherit' }}
          ellipsis={{ tooltip: shownTitle }}
        >
          {shownTitle}
        </Typography.Text>
      </ShowLink>
    </Flex>
  );
};
